import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { usePlatformStatus, useHeadlines, timeAgo } from '@/hooks/useSystemStatus';
import { severityMeta } from '@/lib/severity';
import { ErrorState, LoadingState } from '@/components/primitives';

const fmt = (v) => (typeof v === 'number' ? v.toLocaleString() : '-');

/** The latest real stories from /api/public/headlines, with loading, error and empty states. */
function LatestStoriesCard() {
  const headlines = useHeadlines();
  const { data: status } = usePlatformStatus();
  const rows = (Array.isArray(headlines.data) ? headlines.data : []).slice(0, 4);

  let body;
  if (!headlines.loaded && rows.length === 0) {
    body = <div className="wl-card-state"><LoadingState label="latest stories" rows={4} /></div>;
  } else if (headlines.error && rows.length === 0) {
    body = (
      <div className="wl-card-state">
        <ErrorState variant="inline" title="Couldn't load the latest stories" onRetry={headlines.refresh} />
      </div>
    );
  } else if (rows.length === 0) {
    body = (
      <div className="wl-card-state">
        No stories have been collected yet. Feeds are polled continuously, so check back shortly.
      </div>
    );
  } else {
    body = (
      <ul className="wl-card-list">
        {rows.map((c) => {
          const sev = severityMeta(c.severity);
          return (
            <li key={c.id} className="wl-card-row">
              <span className="wl-card-bar" style={{ background: sev.color }} aria-hidden="true" />
              <div style={{ minWidth: 0 }}>
                <div className="wl-card-name">{c.name}</div>
                <div className="wl-card-meta">
                  <span style={{ color: sev.color, fontWeight: 600 }}>{sev.label}</span>
                  {c.threat_actor && (
                    <>
                      <span className="sep" aria-hidden="true">·</span>
                      <span>Attrib. {c.threat_actor}</span>
                    </>
                  )}
                  <span className="sep" aria-hidden="true">·</span>
                  <span>{timeAgo(c.discovered_at)}</span>
                </div>
              </div>
              {(c.source_count || 1) > 1 ? <span className="is-serial">{c.source_count} sources</span> : <span />}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <section className="wl-card is-framed" aria-labelledby="wl-latest-title">
      <div className="wl-card-ribbon">
        <span className="t-label">Latest from the platform</span>
      </div>
      <div className="wl-card-title">
        <div className="t-label" style={{ color: 'var(--accent-from)' }}>Public headlines</div>
        <h2 id="wl-latest-title">
          What the platform is <em>tracking now.</em>
        </h2>
      </div>
      {body}
      <div className="wl-card-foot">
        <span>
          {status
            ? <><strong>{fmt(status.stories)} stories</strong> from {fmt(status.sources_total)} public sources.</>
            : 'Stories from public advisories and research feeds.'}
        </span>
        <Link to="/register">
          Request access <ArrowUpRight size={13} aria-hidden="true" />
        </Link>
      </div>
      <span className="is-frame-tr" />
      <span className="is-frame-br" />
    </section>
  );
}

/** Platform-wide counts from /api/system/status; nothing here is a marketing figure. */
function PlatformStats() {
  const status = usePlatformStatus();
  const d = status.data;
  const stats = [
    { v: fmt(d?.sources_total), l: 'Public sources', sub: 'Government, vendor and research feeds' },
    { v: fmt(d?.stories), l: 'Stories correlated', sub: 'Duplicate coverage merged into one brief' },
    { v: fmt(d?.cves_tracked), l: 'CVEs tracked', sub: 'Checked against the CISA KEV catalog' },
    { v: d?.last_ingest_at ? timeAgo(d.last_ingest_at) : '-', l: 'Last ingest', sub: 'Feeds are polled continuously' },
  ];

  return (
    <section className="wl-stats" aria-label="Platform figures">
      {stats.map((s) => (
        <div key={s.l} className="wl-stat">
          <div className="wl-stat-value">{s.v}</div>
          <div className="wl-stat-label">{s.l}</div>
          <div className="wl-stat-sub">{s.sub}</div>
        </div>
      ))}
      {status.error && !d && (
        <div className="wl-stats-note">
          <ErrorState variant="inline" title="Platform figures are unavailable right now" onRetry={status.refresh} />
        </div>
      )}
    </section>
  );
}

export default function WelcomeHero() {
  return (
    <section className="wl-hero is-blueprint-bg is-grain" data-testid="welcome-hero">
      <div className="wl-container">
        <div className="wl-hero-grid">
          <div>
            <h1 className="wl-h1">
              Threat intelligence, filtered to <em>the attacks aimed at you.</em>
            </h1>
            <p className="wl-hero-sub">
              IntelliSecure reads public advisories and security research, merges duplicate
              coverage into one brief, and matches each story to your industry, region and
              security stack. Your SOC gets the campaigns that matter, with detection rules ready
              to deploy.
            </p>
            <div className="wl-cta-row">
              <Link to="/register" className="wl-btn wl-btn--primary" data-testid="hero-start-trial-btn">
                Request access
                <ArrowRight aria-hidden="true" />
              </Link>
              <Link to="/login" className="wl-btn wl-btn--ghost" data-testid="hero-signin-btn">
                Sign in
              </Link>
            </div>
            <p className="wl-cta-note">Access is by invitation. MFA available on every account.</p>
          </div>

          <LatestStoriesCard />
        </div>

        <PlatformStats />
      </div>
    </section>
  );
}
